import classNames from "classnames";
import moment from "moment/moment";
import React, { FC, useState } from "react";
import { LuX } from "react-icons/lu";
import { Link } from "react-router-dom";

import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { getInitials, timeFormatter } from "../../../constants/constants";
import { SelectOptionType } from "../../../types/types";
import { InvoiceTime } from "../../time/InvoiceTime";
import {
  BILLING_PRIMARY_BUTTON,
  BILLING_SECONDARY_BUTTON,
  BillingCheckbox,
} from "../ui/BillingUi";

type Props = {
  isLoading: boolean;
  collapsedItems: string[];
  setCollapsedItems: React.Dispatch<React.SetStateAction<string[]>>;
  currentContact: SelectOptionType | null;
  works: InvoiceTime[] | undefined;
  billedWorks: InvoiceTime[];
  onClose: () => void;
  isActive: boolean;
  onAdd: (items: InvoiceTime[]) => void;
};

const GRID =
  "grid grid-cols-[44px_1.5fr_1.1fr_90px_110px_1fr_100px] items-center gap-3";

export const BillableTime: FC<Props> = ({
  isLoading,
  currentContact,
  collapsedItems,
  setCollapsedItems,
  works,
  billedWorks,
  onClose,
  isActive,
  onAdd,
}) => {
  const totalRate = (value: string) => {
    const hours = Number(value.split("h").slice(0, -1));
    const minutes = Number(value?.split("h")?.pop()?.replace("m", "")) / 60;
    return hours + minutes;
  };
  const [billedItem, setBilledItem] = useState<InvoiceTime[]>(billedWorks);
  const sortedWorks = works?.slice().sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });
  const isAllSelected =
    !!sortedWorks?.length && sortedWorks.length === billedItem.length;
  const toggleWork = (work: InvoiceTime) =>
    setBilledItem((prev) =>
      prev.some((item) => item.id === work.id)
        ? prev.filter((item) => item.id !== work.id)
        : [...prev, work],
    );
  const workTotal = (work: InvoiceTime) =>
    Math.trunc(
      (work.assignedUser?.hourlyRate || 0) *
        totalRate(timeFormatter(work.duration)),
    );
  const selectedTotal = billedItem.reduce(
    (sum, work) => sum + workTotal(work),
    0,
  );

  if (!isActive) return null;

  return (
    <div
      className={
        "fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(28,30,45,0.42)] p-8 font-Manrope"
      }
      onClick={onClose}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Add billable time"}
        onClick={(event) => event.stopPropagation()}
        className={
          "flex max-h-[82vh] w-full max-w-[920px] flex-col overflow-hidden rounded-[10px] bg-white shadow-[0_24px_60px_rgba(20,22,34,0.28)]"
        }>
        <div
          className={
            "flex flex-none items-center justify-between border-b border-[#EEF0F3] px-[26px] py-[22px]"
          }>
          <div>
            <h3
              className={
                "m-0 text-lg font-extrabold tracking-[-0.3px] text-[#1F2433]"
              }>
              Add billable time
            </h3>
            <p className={"m-0 mt-[5px] text-[13px] text-[#8B92A2]"}>
              Select time entries to add as line items on this invoice.
            </p>
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "flex h-[34px] w-[34px] items-center justify-center rounded-lg text-[#9097A8] hover:bg-[#F2F3F6]"
            }>
            <LuX size={18} />
          </button>
        </div>

        <div
          className={classNames(
            GRID,
            "flex-none border-b border-[#EEF0F3] bg-[#F7F8FA] px-[26px] py-[13px] text-[11.5px] font-bold uppercase tracking-[0.03em] text-[#8B92A2]",
          )}>
          <BillingCheckbox
            checked={isAllSelected}
            label={"Select all"}
            size={19}
            onChange={(checked) =>
              setBilledItem(checked ? sortedWorks || [] : [])
            }
          />
          <span>Work</span>
          <span>Work Type</span>
          <span>Total Time</span>
          <span>Date</span>
          <span>Assignee</span>
          <span className={"text-right"}>Total</span>
        </div>

        <div className={"flex-1 overflow-y-auto"}>
          {isLoading ? (
            <LottieLoading />
          ) : (
            currentContact &&
            sortedWorks?.map((work) => {
              const isSelected = billedItem.some((item) => item.id === work.id);
              const isNotesOpen = collapsedItems.includes(work?.id);
              const firstName = work.assignedUser?.userProfile?.firstName || "";
              const lastName = work.assignedUser?.userProfile?.lastName || "";

              return (
                <div
                  key={work?.id}
                  onClick={() => toggleWork(work)}
                  className={classNames(
                    GRID,
                    "cursor-pointer border-b border-[#F2F3F6] px-[26px] py-[15px] hover:bg-[#FAF9FF]",
                    isSelected && "bg-[#FAF9FF]",
                  )}>
                  <BillingCheckbox
                    checked={isSelected}
                    label={`Select ${work.work?.name}`}
                    size={19}
                    onChange={() => toggleWork(work)}
                  />
                  <span className={"min-w-0"}>
                    <Link
                      to={`/work/${work?.work?.id}`}
                      onClick={(event) => event.stopPropagation()}
                      className={
                        "text-[13.5px] font-bold text-[#6B4EFF] hover:underline"
                      }>
                      {work.work?.name}
                    </Link>
                    {!!work.notes && (
                      <span
                        title={isNotesOpen ? undefined : work.notes}
                        onClick={(event) => {
                          event.stopPropagation();
                          setCollapsedItems((prev) =>
                            isNotesOpen
                              ? prev.filter((item) => item !== work?.id)
                              : [...prev, work?.id],
                          );
                        }}
                        className={classNames(
                          "mt-0.5 block text-xs text-[#8B92A2]",
                          isNotesOpen ? "whitespace-pre-line" : "truncate",
                        )}>
                        {work.notes}
                      </span>
                    )}
                  </span>
                  <span className={"text-[13px] text-[#3A4256]"}>
                    {work.work?.service?.name}
                  </span>
                  <span
                    className={
                      "text-[13px] font-bold tabular-nums text-[#6B4EFF]"
                    }>
                    {timeFormatter(work.duration)}
                  </span>
                  <span className={"text-[13px] tabular-nums text-[#3A4256]"}>
                    {moment(work.date).format("MM-DD-YYYY")}
                  </span>
                  <span className={"flex min-w-0 items-center gap-2"}>
                    <span
                      className={
                        "flex h-6 w-6 flex-none items-center justify-center rounded-full text-[10px] font-bold text-white"
                      }
                      style={{
                        backgroundColor:
                          (
                            work.assignedUser?.userProfile as
                              | { color?: string }
                              | undefined
                          )?.color || "#1E3A8A",
                      }}>
                      {getInitials(firstName, lastName)}
                    </span>
                    <span className={"truncate text-[13px] text-[#3A4256]"}>
                      {`${firstName} ${lastName}`}
                    </span>
                  </span>
                  <span
                    className={
                      "text-right text-[13.5px] font-bold tabular-nums text-[#1F2433]"
                    }>
                    ${workTotal(work).toFixed(2)}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div
          className={
            "flex flex-none items-center justify-between gap-3.5 border-t border-[#EEF0F3] bg-[#FCFCFD] px-[26px] py-4"
          }>
          <span className={"text-[13px] text-[#6B7185]"}>
            {billedItem.length
              ? `${billedItem.length} ${
                  billedItem.length === 1 ? "entry" : "entries"
                } selected · $${selectedTotal.toFixed(2)}`
              : "No entries selected"}
          </span>
          <div className={"flex items-center gap-3"}>
            <button
              type={"button"}
              onClick={onClose}
              className={BILLING_SECONDARY_BUTTON}>
              Cancel
            </button>
            <button
              type={"button"}
              disabled={!billedItem.length}
              onClick={() => onAdd(billedItem)}
              className={classNames(
                BILLING_PRIMARY_BUTTON,
                "rounded-[9px] px-6 shadow-none",
              )}>
              Add
              {billedItem.length ? ` ${billedItem.length}` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
